import { Paper, Button } from "@mui/material";
import { Grid } from "@material-ui/core";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/Loader/Lodaer";
import { StyledSceneContent } from "../../../components/styledComponents";
import {
  getSingleBillToData,
  saveBillToContactData,
  getRulesApi,
  getInvoiceConfig,
  saveInvoiceConfig,
  deleteInvoiceConfig,
} from "../../../store/actions/maintenanceBillToActions";
import { OrdersWrapper } from "../../../components/styledComponents";
import ContactTable from "./components/allBillToForms/ContactTable";
import PickupDispoForm from "./components/allBillToForms/BillToForm";
import { mapShipperContactToServer } from "./components/commonFn";
import PermissionWrapper from "../../Setting/PermissionWrapper";
import {
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import InvoiceRequirementsSection from "./components/InvoiceRequirementsSection";
import InvoiceDocDeliveryGrid from "./components/InvoiceDocDeliveryGrid";
import BillToRelatedOrderBy from "./components/BillToRelatedOrderBy";
import BillToHistory from "./components/BillToHistory";

// ── Constants ─────────────────────────────────────────────────────────────────

const WARNED_TABS = [0, 1, 2];

const EMPTY_INVOICE_VALUES = {
  invoiceDelivery: {
    deliveryMethod: "",
    requiredDocumentCodes: [],
    config: { emailAddress: "", websiteUrl: "" },
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const mapInvoiceResponse = (res) => {
  if (!res?.delivery_method) return null;
  return {
    deliveryMethod: res.delivery_method,
    addresses: res.delivery_addresses || [],
    requiredDocumentCodes: (res.required_document_codes || []).map(Number),
  };
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function BillToFormContainer(props) {
  const {
    selectedRow,
    addNewPickUpDispo,
    formKey,
    setAddNewPickUpDispo,
    onClose,
    isOrderScreen,
    shipperSelect,
    isEditScreen,
    onEditShipper,
    editDataSendToModal,
  } = props;

  const params = useParams();
  const dispatch = useDispatch();

  // ── Snackbar ───────────────────────────────────────────────────────────────

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackContent, setSnackContent] = useState({ severity: "info", message: "" });

  const showSnack = (severity, message) => {
    setSnackContent({ severity, message });
    setSnackOpen(true);
  };

  // ── Invoice values (synced up from InvoiceRequirementsSection) ─────────────

  const [invoiceValues, setInvoiceValues] = useState(EMPTY_INVOICE_VALUES);

  // ── Edit / loading state ───────────────────────────────────────────────────

  const [editShipper, setEditShipper] = useState(!!isEditScreen);
  const [isLoading, setIsLoading] = useState(params.controlNumber !== "new");

  // ── Contact / form refs ────────────────────────────────────────────────────

  const contactRowsRef = useRef([]);
  const billToFormRef = useRef([]);
  const [contactRows, setContactRows] = useState([]);

  // ── Invoice state ──────────────────────────────────────────────────────────

  const [invoiceInitialData, setInvoiceInitialData] = useState(null);
  const invoiceRef = useRef(null);

  // ── Per-section dirty refs ────────────────────────────────────────────────

  const contactDirtyRef = useRef(false);
  const invoiceDirtyRef = useRef(false);

  // ── Tab state ──────────────────────────────────────────────────────────────

  const [tabValue, setTabValue] = useState(0);
  const [pendingTab, setPendingTab] = useState(null);
  const [warnDialogOpen, setWarnDialogOpen] = useState(false);
  const [tabDirty, setTabDirty] = useState({ 0: false, 1: false, 2: false });

  const markTabDirty = (tabIndex, isDirty) =>
    setTabDirty((prev) => ({ ...prev, [tabIndex]: isDirty }));

  // ── Redux ──────────────────────────────────────────────────────────────────

  const ordersLocation = useSelector(
    ({ orderReducer }) => orderReducer.ordersLocation,
  );

  // ── Tab change ─────────────────────────────────────────────────────────────

  const handleTabChange = (_event, newValue) => {
    if (WARNED_TABS.includes(tabValue) && tabDirty[tabValue]) {
      setPendingTab(newValue);
      setWarnDialogOpen(true);
      return;
    }
    setTabValue(newValue);
  };

  const handleWarnConfirm = () => {
    markTabDirty(tabValue, false);
    setWarnDialogOpen(false);
    setTabValue(pendingTab);
    setPendingTab(null);
  };

  const handleWarnCancel = () => {
    setWarnDialogOpen(false);
    setPendingTab(null);
  };

  // ── Data fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    billToFormRef.current = [];
    contactRowsRef.current = [];
    contactDirtyRef.current = false;
    invoiceDirtyRef.current = false;

    const fetchData = async () => {
      const code = selectedRow || editDataSendToModal?.code || "";

      try {
        const [billToRes, , invoiceRes] = await Promise.all([
          dispatch(
            getSingleBillToData({
              locationNumber: ordersLocation?.locationNumber,
              code,
            }),
          ),
          getRulesApi(),
          dispatch(getInvoiceConfig(code)),
        ]);

        if (!isMounted) return;

        setFormValues(billToRes);
        setInvoiceInitialData(mapInvoiceResponse(invoiceRes));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [formKey, selectedRow, addNewPickUpDispo, shipperSelect]);

  const setFormValues = (mapShipperForm) => {
    if (mapShipperForm) {
      billToFormRef.current = mapShipperForm.shipperForm;
      const contacts = mapShipperForm.shipperContacts || [];
      contactRowsRef.current = contacts;
      setContactRows(contacts);
    } else {
      billToFormRef.current = [];
      contactRowsRef.current = [];
      setContactRows([]);
    }
  };

  // ── Cancel handlers ────────────────────────────────────────────────────────

  const handleCancelClick = () => {
    invoiceRef.current?.cancelEdit();
    contactDirtyRef.current = false;
    invoiceDirtyRef.current = false;
    if (isOrderScreen) onClose();
    else setAddNewPickUpDispo(false);
  };

  const handleCancelEdit = () => {
    invoiceRef.current?.cancelEdit();
    contactDirtyRef.current = false;
    invoiceDirtyRef.current = false;
    if (isOrderScreen) onClose();
    else setAddNewPickUpDispo(false);
    setEditShipper(false);
  };

  // ── Delete invoice ─────────────────────────────────────────────────────────

  const handleDeleteInvoice = async () => {
    const code = billToFormRef.current?.code || "";
    try {
      const res = await dispatch(deleteInvoiceConfig(code));
      if (res?.success || res?.httpCode === 200) {
        const fresh = await dispatch(getInvoiceConfig(code));
        setInvoiceInitialData(mapInvoiceResponse(fresh));
        invoiceDirtyRef.current = false;
        markTabDirty(0, contactDirtyRef.current);
        showSnack("success", "Invoice config deleted.");
      } else {
        showSnack("error", res?.message || "Failed to delete invoice config.");
      }
    } catch {
      showSnack("error", "An error occurred while deleting the invoice config.");
    }
  };

  // ── Payload builder ────────────────────────────────────────────────────────

  const buildInvoiceDeliveryPayload = () => {
    const { invoiceDelivery } = invoiceValues;
    const method = invoiceDelivery.deliveryMethod;

    const payload = {
      deliveryMethod: method,
      requiredDocumentCodes: invoiceDelivery.requiredDocumentCodes?.map(Number) || [],
    };

    if (method === "EMAIL" || method === "EMAIL_INV_ONLY") {
      payload.addresses = (invoiceDelivery.config?.emailAddress || "")
        .split(/[,;]+/)
        .map((e) => e.trim())
        .filter(Boolean);
    }

    if (method === "WEB") {
      payload.addresses = (invoiceDelivery.config?.websiteUrl || "")
        .split(/[,;]+/)
        .map((e) => e.trim())
        .filter(Boolean);
    }

    return payload;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  //
  // Invoice validation is checked via invoiceRef.validateForSave() BEFORE
  // building the task list. If the invoice section has errors the save is
  // blocked early — errors are surfaced inside InvoiceRequirementsSection
  // by the validateForSave call which touches all fields.

  const handleSave = async (type = "new") => {
    const shipperCode = billToFormRef.current?.code;

    if (!shipperCode) {
      showSnack("error", "shipperCode is mandatory!");
      return;
    }

    const shouldSaveContact = contactDirtyRef.current;
    const shouldSaveInvoice = invoiceDirtyRef.current;

    if (!shouldSaveContact && !shouldSaveInvoice) {
      showSnack("info", "No changes to save.");
      return;
    }

    // ── Invoice validation gate ────────────────────────────────────────────
    // Run before firing any API call so the user sees errors immediately
    // without waiting for an unnecessary contact save round-trip.

    if (shouldSaveInvoice) {
      const isInvoiceValid = await invoiceRef.current?.validateForSave();
      if (!isInvoiceValid) {
        showSnack("error", "Please fix the invoice errors before saving.");
        return;
      }
    }

    showSnack("info", `Bill To Data ${type === "new" ? "Saving" : "Updating"} ...`);

    const code = shipperCode;

    const tasks = [];

    if (shouldSaveContact) {
      const postObj = mapShipperContactToServer(
        contactRowsRef.current,
        billToFormRef.current,
        ordersLocation.locationNumber,
      );
      tasks.push({
        section: "contact",
        promise: dispatch(
          saveBillToContactData(ordersLocation.locationNumber, postObj, code),
        ),
      });
    }

    if (shouldSaveInvoice) {
      tasks.push({
        section: "invoice",
        promise: dispatch(saveInvoiceConfig(buildInvoiceDeliveryPayload(), code)),
      });
    }

    const settled = await Promise.allSettled(tasks.map((t) => t.promise));

    const resultsBySection = Object.fromEntries(
      tasks.map((task, i) => [task.section, settled[i]]),
    );

    // ── Contact result ─────────────────────────────────────────────────────

    let contactSaveSucceeded = false;

    if (shouldSaveContact) {
      const result = resultsBySection.contact;
      const value = result.status === "fulfilled" ? result.value : null;

      if (value) {
        const fresh = await dispatch(getSingleBillToData({ code }));
        const contacts = fresh?.shipperContacts || [];
        contactRowsRef.current = contacts;
        setContactRows(contacts);
        contactDirtyRef.current = false;
        contactSaveSucceeded = true;
        markTabDirty(0, invoiceDirtyRef.current);
        showSnack("success", value?.message || "Contact data saved.");
      } else {
        showSnack("error", result.reason?.message || "Failed to save contact data.");
      }
    }

    // ── Invoice result ─────────────────────────────────────────────────────

    let invoiceSaveSucceeded = false;

    if (shouldSaveInvoice) {
      const result = resultsBySection.invoice;
      const value = result.status === "fulfilled" ? result.value : null;

      if (value?.success || value?.httpCode === 200) {
        const freshInvoice = await dispatch(getInvoiceConfig(code));
        setInvoiceInitialData(mapInvoiceResponse(freshInvoice));
        invoiceDirtyRef.current = false;
        invoiceSaveSucceeded = true;
        markTabDirty(0, contactDirtyRef.current);
        showSnack("success", "Invoice config saved.");
      } else {
        showSnack(
          "error",
          value?.message ||
            result.reason?.message ||
            "Failed to save invoice config.",
        );
      }
    }

    // ── Restore view mode only when every dirty section saved successfully ──

    const allDirtySaved =
      (!shouldSaveContact || contactSaveSucceeded) &&
      (!shouldSaveInvoice || invoiceSaveSucceeded);

    if (allDirtySaved) {
      setEditShipper(false);
      onEditShipper(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Paper
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          width: "100%",
          height: "40px",
          backgroundColor: "#E4EEFA",
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", width: "50%" }}>
          {editDataSendToModal?.type ? (
            <label style={{ fontSize: "14px", color: "#091DA3" }}>
              {editDataSendToModal.type} {editDataSendToModal.field} Point
            </label>
          ) : (
            <label style={{ fontSize: "14px", color: "#091DA3" }}>Bill To</label>
          )}
        </div>

        {tabValue === 0 && (addNewPickUpDispo || editShipper) ? (
          <div>
            <Button
              variant="outlined"
              style={{
                width: "80px",
                height: "30px",
                fontSize: "10px",
                marginRight: "10px",
                color: "#000000",
              }}
              startIcon={<SaveIcon style={{ width: "14px", height: "14px" }} />}
              onClick={() => handleSave(editShipper ? "edit" : "new")}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              style={{ width: "80px", height: "30px", fontSize: "10px", color: "#000000" }}
              startIcon={<ClearIcon style={{ width: "14px", height: "14px" }} />}
              onClick={editShipper ? handleCancelEdit : handleCancelClick}
            >
              Cancel
            </Button>
          </div>
        ) : (
          tabValue === 0 && (
            <div>
              <PermissionWrapper category="orderby" level="READ_WRITE">
                <Button
                  variant="outlined"
                  style={{
                    width: "140px",
                    height: "30px",
                    fontSize: "10px",
                    color: "#000000",
                  }}
                  startIcon={<EditIcon style={{ width: "14px", height: "14px" }} />}
                  onClick={() => {
                    setEditShipper(true);
                    onEditShipper(true);
                  }}
                >
                  Edit Bill To
                </Button>
              </PermissionWrapper>
            </div>
          )
        )}
      </div>

      {/* Body */}
      {isLoading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
          }}
        >
          <Loader />
        </div>
      ) : (
        <div>
          <StyledSceneContent
            style={{ border: "1px solid #ccc", overflow: "visible", height: "auto" }}
          >
            <OrdersWrapper style={{ overflow: "visible", height: "auto" }}>
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ overflow: "auto" }}
                  >
                    <Tab label="CUSTOMER INFORMATION" />
                    <Tab label="REQUIRED DOCUMENTS" />
                    <Tab label="RELATED ORDER-BYS" />
                    <Tab label="HISTORY" />
                  </Tabs>
                </Box>

                <Box sx={{ mt: 2, width: "100%" }}>
                  {/* TAB 0 — CUSTOMER INFORMATION */}
                  {tabValue === 0 && (
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={4}
                        lg={5}
                        xl={4}
                        style={{ pointerEvents: "none", opacity: "0.8" }}
                      >
                        <PickupDispoForm billToFormRef={billToFormRef} />
                      </Grid>
                      <Grid item xs={12} sm={12} md={8} lg={7} xl={8}>
                        <div style={{ height: "100%" }}>
                          <Grid container style={{ height: "100%" }}>
                            <Grid
                              item
                              xs={12}
                              style={
                                editShipper || addNewPickUpDispo
                                  ? {}
                                  : { pointerEvents: "none", opacity: "0.8" }
                              }
                            >
                              <ContactTable
                                contactRows={contactRows}
                                onRowsChange={(updatedRows) => {
                                  contactRowsRef.current = updatedRows;
                                  setContactRows(updatedRows);
                                  contactDirtyRef.current = true;
                                  markTabDirty(0, true);
                                }}
                                code={billToFormRef?.current?.code || ""}
                              />
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ width: "100%" }}>
                          <InvoiceRequirementsSection
                            ref={invoiceRef}
                            isEditMode={editShipper || addNewPickUpDispo}
                            setInvoiceValues={setInvoiceValues}
                            onDirtyChange={(isDirty) => {
                              invoiceDirtyRef.current = isDirty;
                              markTabDirty(0, isDirty || contactDirtyRef.current);
                            }}
                            initialData={invoiceInitialData}
                            onDelete={handleDeleteInvoice}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                  {/* TAB 1 — REQUIRED DOCUMENTS */}
                  {tabValue === 1 && (
                    <Box sx={{ width: "100%", p: 2 }}>
                      <InvoiceDocDeliveryGrid
                        billToCode={billToFormRef?.current?.code || ""}
                        onDirtyChange={(isDirty) => markTabDirty(1, isDirty)}
                      />
                    </Box>
                  )}

                  {/* TAB 2 — RELATED ORDER-BYS */}
                  {tabValue === 2 && (
                    <Box sx={{ width: "100%" }}>
                      <BillToRelatedOrderBy
                        billToCode={billToFormRef?.current?.code}
                      />
                    </Box>
                  )}

                  {/* TAB 3 — HISTORY */}
                  {tabValue === 3 && (
                    <Box sx={{ height: "400px" }}>
                      <BillToHistory code={billToFormRef.current?.code} />
                    </Box>
                  )}
                </Box>
              </Box>
            </OrdersWrapper>
          </StyledSceneContent>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        style={{ marginTop: "5%" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackOpen(false)}
        autoHideDuration={4000}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackContent.severity}
          sx={{ width: "100%" }}
        >
          {snackContent.message}
        </Alert>
      </Snackbar>

      {/* Unsaved changes warning dialog */}
      <Dialog open={warnDialogOpen} onClose={handleWarnCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.90rem" }}>
            You have unsaved changes on this tab. If you leave now, your changes will be
            lost. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleWarnCancel}
            variant="outlined"
            size="small"
            style={{ fontSize: "0.85rem", color: "#000" }}
          >
            Stay
          </Button>
          <Button
            onClick={handleWarnConfirm}
            variant="contained"
            size="small"
            style={{ fontSize: "0.85rem", background: "rgb(11, 67, 135)" }}
          >
            Leave Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
